import { Stack, Typography, chipClasses, styled } from "@mui/material";
import { CLAMP_LINES } from "./reviewChanges.consts";

// Stays at the top while the review scrolls, so filtering or switching the base never needs a scroll back.
// Sticks over DialogContent's 20px top padding too, or the rows scroll through that strip above it.
export const TopBar = styled(Stack)(({ theme }) => ({
  position: "sticky",
  top: -20,
  zIndex: 2,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
  paddingBlock: 8,
  backgroundColor: theme.palette.system[0]
}));

export const CompareTo = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: 12,
  paddingLeft: 12,
  borderLeft: `1px solid ${theme.palette.system[300]}`
}));

// A count next to a group title or in a tab label.
export const COUNT_CHIP_SX = {
  height: 20,
  fontWeight: 600,
  [`& .${chipClasses.label}`]: { padding: "0 7px" }
};

export const EntryHeader = styled(Stack, { shouldForwardProp: (prop) => prop !== "expandable" })(({ theme, expandable }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: 10,
  minHeight: 44,
  padding: "6px 8px 6px 12px",
  borderRadius: 8,
  backgroundColor: theme.palette.system[50],
  cursor: expandable ? "pointer" : "default"
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.text[700]
}));

// "Field  [WAS] → [NOW]  ↗" on one line; on a narrow screen the label goes above the boxes.
export const FieldRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
  [theme.breakpoints.down("sm")]: { alignItems: "stretch" }
}));

export const FieldLabel = styled(Typography)(({ theme }) => ({
  width: 200,
  flexShrink: 0,
  fontSize: 14,
  color: theme.palette.text[800],
  wordBreak: "break-word",
  [theme.breakpoints.down("sm")]: { width: "100%" }
}));

export const FieldValueBox = styled(Stack, { shouldForwardProp: (prop) => prop !== "current" })(({ theme, current }) => ({
  flex: 1,
  minWidth: 160,
  alignSelf: "stretch",
  gap: 2,
  padding: "8px 12px",
  borderRadius: 8,
  border: `1px solid ${current ? theme.palette.secondary[400] : theme.palette.system[300]}`,
  backgroundColor: current ? theme.palette.system[0] : theme.palette.system[100]
}));

export const ChangedCell = styled(Stack)(({ theme }) => ({
  alignSelf: "stretch",
  justifyContent: "flex-start",
  gap: 2,
  margin: "-6px -10px",
  padding: "10px",
  backgroundColor: theme.palette.tertiary[100]
}));

export const DiffLabel = styled("span")(({ theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.04em",
  marginRight: 6,
  color: theme.palette.text[700]
}));

export const NowLabel = styled(DiffLabel)(({ theme }) => ({
  color: theme.palette.primary[500]
}));

export const CellText = styled(Typography, { shouldForwardProp: (prop) => !["clamped", "muted"].includes(prop) })(({ theme, clamped, muted }) => ({
  fontSize: muted ? 12 : 14,
  color: muted ? theme.palette.text[700] : theme.palette.text[800],
  whiteSpace: "pre-line",
  wordBreak: "break-word",
  ...(clamped ? { display: "-webkit-box", WebkitLineClamp: CLAMP_LINES, WebkitBoxOrient: "vertical", overflow: "hidden" } : {})
}));

export const PlaceholderText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontStyle: "italic",
  color: theme.palette.text[700]
}));

export const IdText = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontFamily: "monospace",
  color: theme.palette.text[700]
}));

export const DeletedText = styled("span")(({ theme }) => ({
  backgroundColor: theme.palette.error[100],
  color: theme.palette.error[700],
  textDecoration: "line-through"
}));

export const InsertedText = styled("span")(({ theme }) => ({
  backgroundColor: theme.palette.success[100],
  color: theme.palette.success[700]
}));
